import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Home, Users, Mail, User, Key, Eye, EyeOff, Phone, MapPin } from 'lucide-react';
import { CustomBreadcrumb, CustomInput, CustomButton } from '@/shared/components/atoms';
import { CustomCommonScreenHeader } from '@/shared/components/molecules';
import { parentApi } from '../api/parentApi';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';

const addParentSchema = z.object({
    email: z.string().email('Invalid email address'),
    name: z.string().min(2, 'Name must be at least 2 characters'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    phone_number: z.string()
        .min(1, 'Phone number is required')
        .regex(/^[+]?[0-9]{10,15}$/, 'Phone number must be between 10 and 15 digits'),
    secondary_phone: z.string()
        .min(1, 'Secondary phone number is required')
        .regex(/^[+]?[0-9]{10,15}$/, 'Secondary phone number must be between 10 and 15 digits'),
    address: z.string().min(1, 'Address is required'),
});

type AddParentFormData = z.infer<typeof addParentSchema>;

export const AddParent = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { showToast } = useToastStore();

    const schoolSlug = user?.school?.slug || '';

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<AddParentFormData>({
        resolver: zodResolver(addParentSchema)
    });

    const onSubmit = async (data: AddParentFormData) => {
        if (!schoolSlug) {
            showToast('School context not found', 'error');
            return;
        }

        setIsLoading(true);
        try {
            const response = await parentApi.inviteParent(schoolSlug, data);
            if (response.success) {
                showToast('Parent added successfully!', 'success');
                navigate(`/${schoolSlug}/school-admin/parents`);
            } else {
                showToast(response.error || 'Failed to add parent', 'error');
            }
        } catch (error) {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ padding: '1rem 1.5rem' }}>
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Parents', path: `/${schoolSlug}/school-admin/parents`, icon: <Users size={14} /> },
                    { label: 'Add Parent' }
                ]}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <CustomCommonScreenHeader
                    title="Add New Parent"
                    subtitle="Please add details of parent"
                    icon={<Users size={24} />}
                />
            </div>

            <div style={{
                maxWidth: '700px',
                margin: '2rem auto',
                background: 'var(--card-bg)',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
                padding: '2.5rem',
                boxShadow: 'var(--card-shadow)'
            }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Full Name"
                                placeholder="Enter parent name"
                                leftIcon={<User size={18} />}
                                error={errors.name?.message}
                                {...register('name')}
                                required
                            />
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Email Address"
                                type="email"
                                placeholder="parent@example.com"
                                leftIcon={<Mail size={18} />}
                                error={errors.email?.message}
                                {...register('email')}
                                required
                            />
                        </div>

                        <div style={{ gridColumn: 'span 1' }}>
                            <CustomInput
                                label="Phone Number"
                                type="tel"
                                placeholder="Enter phone number"
                                leftIcon={<Phone size={18} />}
                                error={errors.phone_number?.message}
                                {...register('phone_number')}
                                required
                            />
                        </div>

                        <div style={{ gridColumn: 'span 1' }}>
                            <CustomInput
                                label="Secondary Phone"
                                type="tel"
                                placeholder="Enter secondary phone number"
                                leftIcon={<Phone size={18} />}
                                error={errors.secondary_phone?.message}
                                {...register('secondary_phone')}
                                required
                            />
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Address"
                                type="text"
                                placeholder="Enter physical address"
                                leftIcon={<MapPin size={18} />}
                                error={errors.address?.message}
                                {...register('address')}
                                required
                            />
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Initial Password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Set initial password"
                                leftIcon={<Key size={18} />}
                                rightIcon={
                                    <div 
                                        onClick={() => setShowPassword(!showPassword)} 
                                        style={{ cursor: 'pointer', color: 'var(--text-muted)' }}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </div>
                                }
                                error={errors.password?.message}
                                {...register('password')}
                                required
                                helperText="Parent can change this password later."
                            />
                        </div>

                        <div style={{
                            gridColumn: 'span 2',
                            marginTop: '2rem',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '1rem',
                            borderTop: '1px solid var(--border-color)',
                            paddingTop: '2rem'
                        }}>
                            <CustomButton
                                variant="outline"
                                color="secondary"
                                onClick={() => navigate(-1)}
                                disabled={isLoading}
                            >
                                Cancel
                            </CustomButton>
                            <CustomButton
                                type="submit"
                                variant="filled"
                                color="primary"
                                loading={isLoading}
                            >
                                Add Parent
                            </CustomButton>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
